<template>
  <div class="card border mb-3" data-testid="sale-notices">
    <div
      class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <div>
        <h6 class="mb-0">
          <button
            type="button"
            class="btn btn-link p-0 d-inline-flex align-items-center gap-2 text-decoration-none text-body notices-toggle"
            data-testid="toggle-notices"
            :aria-expanded="open"
            :aria-controls="bodyId"
            @click="open = !open"
          >
            <FontAwesomeIcon :icon="open ? 'chevron-down' : 'chevron-right'" />
            <span>{{ $t("forwards.deals.sale_notices.title") }}</span>
            <span
              class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
              data-testid="notice-count"
              >{{ notices.length }}</span
            >
          </button>
        </h6>
        <span v-if="open" class="text-body-tertiary fs-10">{{
          $t("forwards.deals.sale_notices.subtitle")
        }}</span>
      </div>
      <button
        v-if="canSkip && open"
        type="button"
        class="btn btn-sm btn-outline-danger"
        data-testid="skip-selected"
        :disabled="selectedIds.length === 0 || busy"
        @click="emit('skip', selectedIds)"
      >
        {{ $t("forwards.deals.sale_notices.skip_selected") }}
      </button>
    </div>
    <div v-if="open" :id="bodyId" class="card-body">
      <p
        v-if="notSkippedCount > 0"
        class="advisory-note mb-3"
        role="status"
        data-testid="not-skipped"
      >
        <FontAwesomeIcon icon="fa-regular fa-circle-info" />
        <span>{{
          $t("forwards.deals.sale_notices.not_skipped", notSkippedCount)
        }}</span>
      </p>
      <table class="table table-sm mb-0 align-top">
        <thead>
          <tr>
            <th v-if="canSkip" scope="col">
              <input
                type="checkbox"
                class="form-check-input"
                data-testid="select-all"
                :aria-label="$t('forwards.deals.sale_notices.select_all')"
                :checked="allSelected"
                :indeterminate="someSelected"
                @change="toggleAll"
              />
            </th>
            <th scope="col">{{ $t("forwards.deals.sale_notices.mandate") }}</th>
            <th scope="col">
              {{ $t("forwards.deals.sale_notices.states") }}
            </th>
            <th scope="col">
              {{ $t("forwards.deals.sale_notices.goes_out") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="notice in notices"
            :key="notice.mandate_id"
            data-testid="sale-notice-row"
          >
            <td v-if="canSkip">
              <input
                v-model="selectedIdsModel"
                type="checkbox"
                class="form-check-input"
                :value="notice.mandate_id"
                :aria-label="
                  $t('forwards.deals.sale_notices.select', {
                    mandate: notice.mandate_name,
                  })
                "
              />
            </td>
            <td class="fw-semibold">{{ notice.mandate_name }}</td>
            <td>
              <table class="table table-sm table-borderless mb-0 fs-10">
                <thead>
                  <tr>
                    <th scope="col">
                      {{ $t("forwards.deals.sale_notices.delivery") }}
                    </th>
                    <th scope="col" class="text-end">
                      {{ $t("forwards.deals.sale_notices.sold") }}
                    </th>
                    <th scope="col" class="text-end">
                      {{ $t("forwards.deals.sale_notices.change") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in notice.rows" :key="index">
                    <td>{{ row.delivery }}</td>
                    <td class="text-end text-nowrap">{{ row.sold }}</td>
                    <td class="text-end text-nowrap">{{ row.change }}</td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td class="text-nowrap">
              <div>{{ formatDate(notice.sends_on) }}</div>
              <div class="text-body-tertiary fs-10">
                {{
                  $t(
                    notice.channel === "monthly"
                      ? "forwards.deals.sale_notices.monthly_email"
                      : "forwards.deals.sale_notices.daily_email"
                  )
                }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from "@/composables/formatUtils";
import type { UnsentSaleNotice } from "@/forwards2/forwardsTypes";
import { computed, ref, useId } from "vue";

/**
 * The deal's unsent sale notices, one per mandate, rendered by the backend exactly as their emails will
 * read, so staff can review them while the deal settles. Starts collapsed to its header, which counts
 * the notices. A super admin may skip a batch: the page confirms before the skip is written. The selection only ever names mandates still listed, so a reload
 * that drops one (sent meanwhile, or the plan agrees again) drops it from the selection too.
 */
const props = withDefaults(
  defineProps<{
    notices: UnsentSaleNotice[];
    canSkip: boolean;
    busy?: boolean;
    /** Mandates the last skip found with nothing unsent; the server left them as they are. */
    notSkippedCount?: number;
  }>(),
  { busy: false, notSkippedCount: 0 }
);

const emit = defineEmits<{
  skip: [mandateIds: number[]];
}>();

const bodyId = useId();
const open = ref(false);
const picked = ref<number[]>([]);

const listedIds = computed(() =>
  props.notices.map((notice) => notice.mandate_id)
);

const selectedIds = computed(() =>
  picked.value.filter((mandateId) => listedIds.value.includes(mandateId))
);

const selectedIdsModel = computed({
  get: () => selectedIds.value,
  set: (mandateIds: number[]) => {
    picked.value = mandateIds;
  },
});

const allSelected = computed(
  () =>
    listedIds.value.length > 0 &&
    selectedIds.value.length === listedIds.value.length
);

const someSelected = computed(
  () => selectedIds.value.length > 0 && !allSelected.value
);

const toggleAll = () => {
  picked.value = allSelected.value ? [] : [...listedIds.value];
};
</script>

<style scoped lang="scss">
// The theme sets buttons at 0.8rem; the toggle reads as the card's heading.
.notices-toggle {
  font-size: inherit;
  font-weight: inherit;
}
</style>
